<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                {{ editingTransfer ? "Edit Asset Transfer" : "Add New Asset Transfer" }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit" ref="form">
                  <v-row class="pa-1 py-2 px-2">
                    <!-- Transfer Date -->
                    <v-col md="4" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="transfer_date.value.value"
                        label="Transfer Date *"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        :error-messages="transfer_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <!-- From Location -->
                    <v-col md="4" cols="12">
                      <v-autocomplete
                        v-model="form_asset_location_id.value.value"
                        label="From Location *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="getAssetLocationsList"
                        :item-title="(item) => `${item.name}`"
                        item-value="id"
                        :error-messages="form_asset_location_id.errorMessage.value"
                        :loading="locationLoading"
                        @update:model-value="onFromLocationChange"
                        prepend-inner-icon="mdi-map-marker"
                      ></v-autocomplete>
                    </v-col>

                    <!-- To Location -->
                    <v-col md="4" cols="12">
                      <v-autocomplete
                        v-model="to_asset_location_id.value.value"
                        label="To Location *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="getAssetLocationsList"
                        :item-title="(item) => `${item.name}`"
                        item-value="id"
                        :error-messages="to_asset_location_id.errorMessage.value"
                        :loading="locationLoading"
                        prepend-inner-icon="mdi-map-marker-outline"
                      ></v-autocomplete>
                    </v-col>

                    <!-- Remarks -->

                  </v-row>

                  <!-- Assets Selection Section -->
                  <v-row class="py-2 px-2">
                    <v-col cols="12">
                      <v-card variant="outlined" class="pa-4">
                        <v-card-title class="pa-2">
                          <span class="text-h6">Select Assets</span>
                        </v-card-title>
                        <v-card-text>
                          <!-- Asset Search -->
                          <v-row class="pb-2">
                            <v-col md="6" cols="12">
                              <v-autocomplete
                                v-model="selectedAssetId"
                                label="Search and Add Asset"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                :items="availableAssets"
                                :item-title="(item) => `${item.code} - ${item.name}`"
                                item-value="id"
                                :loading="assetLoading"
                                return-object
                                prepend-inner-icon="mdi-magnify"
                                clearable
                              >
                                <template v-slot:item="{ props, item }">
                                  <v-list-item v-bind="props">
                                    <template v-slot:title>
                                      <div>
                                        <strong>{{ item.raw.code }}</strong> - {{ item.raw.name }}
                                      </div>
                                      <div class="text-caption text-grey">
                                        Category: {{ item.raw.asset_category?.name }} | 
                                        Location: {{ item.raw.asset_location?.name }} | 
                                        Status: {{ item.raw.asset_status?.name }}
                                      </div>
                                    </template>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                            <v-col md="2" cols="12">
                              <v-btn
                                color="primary"
                                block
                                @click="addAssetToTransfer"
                                :disabled="!selectedAssetId"
                                prepend-icon="mdi-plus"
                              >
                                Add Asset
                              </v-btn>
                            </v-col>
                          </v-row>

                          <!-- Selected Assets Table -->
                          <v-data-table
                            :headers="assetHeaders"
                            :items="transferAssets"
                            :items-per-page="5"
                            density="compact"
                            class="elevation-1"
                          >
                            <template v-slot:item.code="{ item }">
                              <strong>{{ item.code }}</strong>
                            </template>
                            <template v-slot:item.currentLocation="{ item }">
                              <v-chip size="small" color="orange" variant="tonal">
                                {{ item.asset_location?.name }}
                              </v-chip>
                            </template>
                            <template v-slot:item.status="{ item }">
                              <v-chip size="small" color="blue" variant="tonal">
                                {{ item.asset_status?.name }}
                              </v-chip>
                            </template>
                            <template v-slot:no-data>
                              <div class="text-center py-4">
                                <v-icon size="48" color="grey">mdi-package-variant</v-icon>
                                <p class="text-grey">No assets added yet</p>
                              </div>
                            </template>
                          </v-data-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-col cols="12">
                      <v-textarea
                        v-model="remarks.value.value"
                        label="Remarks"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        rows="2"
                        :error-messages="remarks.errorMessage.value"
                      ></v-textarea>
                    </v-col>

                  <!-- Action Buttons -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <div class="d-flex justify-end" style="gap: 12px;">
                        <v-btn
                          class="bg-grey-darken-1 me-2"
                          variant="flat"
                          type="button"
                          :loading="saving && currentAction === 0"
                          :disabled="saving"
                          @click="submitForm(0)"
                        >
                          DRAFT
                        </v-btn>
                        <v-btn
                          color="primary"
                          type="button"
                          :loading="saving && currentAction === 1"
                          :disabled="saving && currentAction !== 1"
                          prepend-icon="mdi-content-save"
                          @click="submitForm(1)"
                        >
                          {{ editingTransfer ? "Update & Post" : "POST" }}
                        </v-btn>
                      </div>
                    </v-col>

                    
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import { getAssetRegistrationsList } from "@/store/api";
import Axios from "axios";

export default {
  components: {},
  data() {
    return {
      panel: [0],
      locationLoading: false,
      assetLoading: false,
      saving: false,
      currentAction: null,
      currentStatus: 1,
      selectedAssetId: null,
      transferAssets: [],
      allAssets: [],
    assetHeaders: [
      { title: "Code", key: "code", align: "left", sortable: false },
      { title: "Name", key: "name", align: "left", sortable: false },
      { title: "Category", key: "asset_category.name", align: "left", sortable: false },
      { title: "Current Location", key: "currentLocation", align: "left", sortable: false },
      { title: "Status", key: "status", align: "left", sortable: false },
    ],
    };
  },
  computed: {
    editingTransfer() {
      return this.$store.getters.getEditingAssetTransfer;
    },
    getAssetLocationsList() {
      let data = this.$store.getters.getAssetLocationsList;
      if (data.length > 0) {
        return data.filter((item) => item.status == 1);
      }
      return [];
    },
    availableAssets() {
      // Filter assets that can be transferred (not already selected and from the selected location)
      return this.allAssets.filter((asset) => {
        if (!this.form_asset_location_id?.value?.value) return false;
        // Only show assets from the selected "from" location
        if (asset.asset_location_id !== this.form_asset_location_id.value.value) return false;
        // Don't show already selected assets
        return !this.transferAssets.some((selected) => selected.id === asset.id);
      });
    },
  },
  watch: {
    editingTransfer: {
      handler(newVal) {
        if (newVal && newVal.id) {
          // Always fetch fresh data for editing to ensure all relationships are loaded
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadLocations();
    this.loadAssets();
  },
  setup() {
    const store = useStore();

    const validationSchema = yup.object({
      transfer_date: yup.string().required("Transfer date is required"),
      form_asset_location_id: yup.number().required("From location is required"),
      to_asset_location_id: yup
        .number()
        .required("To location is required")
        .test("different-location", "To location must be different from From location", function (value) {
          return value !== this.parent.form_asset_location_id;
        }),
      remarks: yup.string().nullable(),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const transfer_date = useField("transfer_date");
    const form_asset_location_id = useField("form_asset_location_id");
    const to_asset_location_id = useField("to_asset_location_id");
    const remarks = useField("remarks");
    const status = useField("status");

    return {
      transfer_date,
      form_asset_location_id,
      to_asset_location_id,
      remarks,
      handleSubmit,
      resetForm,
    };
  },
  methods: {
    async submitForm(status) {
      this.currentStatus = typeof status !== "undefined" ? status : 1;
      this.currentAction = this.currentStatus;
      try {
        await this.submit();
      } finally {
        this.currentAction = null;
      }
    },
    async submit() {
      try {
        // Check if at least one asset is selected
        if (this.transferAssets.length === 0) {
          toast.error("Please add at least one asset to transfer");
          return;
        }

        // Validate and submit
        await this.handleSubmit(async (values) => {
          this.saving = true;
          const transferData = {
            transfer_date: values.transfer_date,
            form_asset_location_id: values.form_asset_location_id,
            to_asset_location_id: values.to_asset_location_id,
            remarks: values.remarks,
            status: this.currentStatus ?? 1,
            asset_transfer_master_id: this.editingTransfer?.id || null,
            assets: this.transferAssets.map((asset) => ({
              asset_register_id: asset.id,
              remarks: asset.remarks || null,
            })),
          };

          try {
            const response = await this.$store.dispatch("saveUpdateAssetTransfer", transferData);
            if (response.data.success) {
              toast.success(response.data.message || "Asset transfer saved successfully");
              this.$store.commit("UNSET_EDITING_ASSET_TRANSFER");
              this.$router.push({ name: "transfer-records" });
            } else {
              toast.error(response.data.message || "Failed to save asset transfer");
            }
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save asset transfer");
          } finally {
            this.saving = false;
          }
        })();
      } catch (error) {
        console.error("Validation error:", error);
      }
    },
    loadLocations() {
      if (this.$store.getters.getAssetLocationsList.length == 0) {
        this.locationLoading = true;
        this.$store.dispatch("getAssetLocationsList").then(() => {
          this.locationLoading = false;
        });
      }
    },
    async loadAssets() {
      this.assetLoading = true;
      try {
        const response = await Axios.get(getAssetRegistrationsList, {
          params: {},
        });
        this.allAssets = response.data.data || [];
      } catch (error) {
        console.error("Error loading assets:", error);
        this.allAssets = [];
      } finally {
        this.assetLoading = false;
      }
    },
    async loadEditDataById(id) {
      try {
        // Ensure assets are loaded first
        if (this.allAssets.length === 0) {
          await this.loadAssets();
        }
        
        const response = await this.$store.dispatch("getAssetTransferById", id);
        console.log("Transfer data response:", response);
        if (response && response.data) {
          this.loadEditData(response.data);
        }
      } catch (error) {
        console.error("Error loading transfer data:", error);
        toast.error("Failed to load transfer data");
      }
    },
    loadEditData(transfer) {
      if (transfer) {
        console.log("Loading edit data:", transfer);
        this.transfer_date.value.value = transfer.transfer_date;
        this.form_asset_location_id.value.value = transfer.form_asset_location_id;
        this.to_asset_location_id.value.value = transfer.to_asset_location_id;
        this.remarks.value.value = transfer.remarks || "";

        // Load existing transfer assets with full details
        if (transfer.details && transfer.details.length > 0) {
          console.log("Transfer details:", transfer.details);
          this.transferAssets = transfer.details.map((detail) => {
            // If assetRegister is not loaded properly, we need to find it from allAssets
            if (detail.assetRegister && detail.assetRegister.id) {
              return detail.assetRegister;
            } else if (detail.asset_register_id) {
              // Try to find the asset from the loaded assets list
              const asset = this.allAssets.find(a => a.id === detail.asset_register_id);
              return asset;
            }
            return null;
          }).filter(Boolean);
          console.log("Loaded transfer assets:", this.transferAssets);
        }
      }
    },
    onFromLocationChange() {
      // Clear selected assets when from location changes
      this.transferAssets = [];
      this.selectedAssetId = null;
    },
    addAssetToTransfer() {
      if (!this.selectedAssetId) {
        toast.error("Please select an asset");
        return;
      }

      // Check if asset is already added
      if (this.transferAssets.some((asset) => asset.id === this.selectedAssetId.id)) {
        toast.warning("This asset is already added");
        return;
      }

      this.transferAssets.push(this.selectedAssetId);
      this.selectedAssetId = null;
      toast.success("Asset added successfully");
    },
    removeAssetFromTransfer(asset) {
      const index = this.transferAssets.findIndex((a) => a.id === asset.id);
      if (index > -1) {
        this.transferAssets.splice(index, 1);
        toast.success("Asset removed");
      }
    },
    goBack() {
      this.$store.commit("UNSET_EDITING_ASSET_TRANSFER");
      this.$router.push({ name: "transfer-records" });
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

